import * as yup from 'yup';
import { ChangeEvent } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';

import {
  GetEventsForMailPermissionsQuery as GetEventsForMailPermissions,
  GetMailPermissionsQuery as GetMailPermissions,
  MailPermissionIntegrationConfigInput,
} from '../../../__generated__/graphql';

import { TranslatableInput } from '../../Translations/Inputs';
import { getServerErrors } from '../../../common/helpers';
import { translationsForAttribute } from '../../Translations/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import EditMailPermissionMutation from './EditMailPermissionMutation';
import GetEventsForMailPermissionsQuery from './GetEventsForMailPermissionsQuery';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useScroll from '../../../common/useScroll';
import useValidation from '../../../common/useValidation';

const editMailPermissionSchema = yup.object().shape({
  title: yup.string().ensure().required(),
  label: yup.string().ensure().required(),
});

type MailPermission = GetMailPermissions['project']['mail_permissions'][0];
type Integration = GetEventsForMailPermissions['project']['integrations'][0];

export interface EditMailPermissionFormProps {
  mailPermission: MailPermission;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditMailPermissionForm = ({ mailPermission, onSuccess, onCancel }: EditMailPermissionFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();
  const { scrollTo } = useScroll();

  const getValues = (mailPermission: MailPermission) => ({
    title: mailPermission.title,
    title_translations: translationsForAttribute(mailPermission.translations, 'title'),
    label: mailPermission.label,
    label_translations: translationsForAttribute(mailPermission.translations, 'label'),
    events: mailPermission.events.map(({ id }) => id),
    soft_opt_in: mailPermission.soft_opt_in,
    integration_id: mailPermission.integration?.id,
    integration_config: mailPermission.integration_config ? {
      mailchimp: mailPermission.integration_config.mailchimp ? {
        audience_id: mailPermission.integration_config.mailchimp.audience_id,
      } : undefined,
      active_campaign: mailPermission.integration_config.active_campaign ? {
        list_id: mailPermission.integration_config.active_campaign.list_id,
      } : undefined,
    } : null,
  });

  const form = useForm({
    values: getValues(mailPermission),
  });

  const [editMailPermission, { loading, error }] = useMutation(
    EditMailPermissionMutation,
    {
      variables: {
        input: {
          id: mailPermission.id,
          ...form.values,
        },
      },
      onCompleted: ({ editMailPermission: data }) => {
        onSuccess?.();
        notifier.success(t('notifications.mail_permission_saved'));
        form.reset(getValues(data));
      },
      onError: () => {
        scrollTo(`EditMailPermissionForm_${mailPermission.id}`);
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editMailPermissionSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { data } = useQuery(
    GetEventsForMailPermissionsQuery, {
      variables: {
        id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const events = data?.project.events.data || [];
  const integrations = data?.project.integrations || [];

  const selectAllEvents = () => {
    form.set('events', events.map((event) => event.id));
  };

  const deselectAllEvents = () => {
    form.set('events', []);
  };

  const keyedIntegrations = keyBy(integrations, 'id');

  const integration = integrations.filter((integration) => integration.id === form.values.integration_id)[0];

  const getIntegrationConfig = (integration: Integration): MailPermissionIntegrationConfigInput | null => {
    if (integration.mailchimp) {
      return {
        mailchimp: {
          audience_id: integration.mailchimp.audiences[0]?.id,
        },
        active_campaign: undefined,
      };
    }

    if (integration.active_campaign) {
      return {
        mailchimp: undefined,
        active_campaign: {
          list_id: integration.active_campaign.lists[0]?.id,
        },
      };
    }

    return null;
  };

  const handleIntegrationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedIntegration = keyedIntegrations[event.target.value];

    if (selectedIntegration) {
      form.set('integration_id', selectedIntegration.id);
      form.set('integration_config', getIntegrationConfig(selectedIntegration));
    } else {
      form.set('integration_id', null);
      form.set('integration_config', null);
    }
  };

  if (!data) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Form
      onSubmit={() => editMailPermission()}
      id={`EditMailPermissionForm_${mailPermission.id}`}
    >
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="MailPermission_Title">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('mail_permission_title_description')}
          </UI.InputDescription>
          <TranslatableInput
            form={form}
            errors={errors}
            name="title"
            defaultLocale={project.locale}
            title={t('title')}
            maxLength={255}
            placeholder={t('mail_permission_title_placeholder')}
            id="MailPermission_Title"
            autoFocus={!mailPermission}
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.label, touched: form.touched.label }}>
          <UI.InputLabel htmlFor="MailPermission_Label">
            {t('mail_permission_label')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('mail_permission_label_description')}
          </UI.InputDescription>
          <TranslatableInput
            form={form}
            errors={errors}
            name="label"
            defaultLocale={project.locale}
            title={t('mail_permission_label')}
            maxLength={255}
            placeholder={t('mail_permission_label_placeholder')}
            id="MailPermission_Label"
          />
          <UI.ErrorMessages attribute={t('label')} errors={errors.label} />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel>
            {t('soft_opt_in')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('collect_soft_opt_ins_description')}
          </UI.InputDescription>
          <UI.Checkbox {...form.checkbox('soft_opt_in')}>
            {t('collect_soft_opt_ins_label')}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.events, touched: form.touched.events }}>
          <UI.InputLabel>
            {t('collect_opt_ins_for')}
            <UI.Span sc={{ muted: true }}> • </UI.Span>
            <UI.A
              onClick={() => selectAllEvents()}
              sc={{ disabled: form.values.events.length === events.length }}
            >
              {t('select_all')}
            </UI.A>
            <UI.Span sc={{ muted: true }}> • </UI.Span>
            <UI.A
              onClick={() => deselectAllEvents()}
              sc={{ disabled: form.values.events.length === 0 }}
            >
              {t('deselect_all')}
            </UI.A>
          </UI.InputLabel>
          {events.map((event) => (
            <UI.Checkbox
              {...form.checklist('events', event.id)}
              key={event.id}
            >
              {event.title}
            </UI.Checkbox>
          ))}
        </UI.InputGroup>

        {integrations.length > 0 && (
          <UI.InputGroup sc={{ valid: !errors.integration_id, touched: form.touched.integration_id }}>
            <UI.InputLabel htmlFor="MailPermission_Integration">
              {t('integration')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('mail_permission_integration_description')}
            </UI.InputDescription>
            <UI.Select
              value={form.values.integration_id || ''}
              onChange={handleIntegrationChange}
              sc={{ placeholder: !form.values.integration_id }}
              id="MailPermission_Integration"
            >
              <option value="">{t('none')}</option>
              {integrations.map((integration) => (
                <option value={integration.id} key={integration.id}>
                  {`${t(`integration_types.${integration.type}`)} (${integration.title})`}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('integration')} errors={errors.integration_id} />
          </UI.InputGroup>
        )}

        {integration?.mailchimp && (
          <UI.InputGroup sc={{
            valid: !errors.integration_config?.mailchimp?.audience_id,
            touched: form.touched.integration_config?.mailchimp?.audience_id,
          }}
          >
            <UI.InputLabel htmlFor="MailPermission_Mailchimp_Audience">
              {t('mailchimp_audience')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.Select
              {...form.select(
                'integration_config.mailchimp.audience_id',
                integration.mailchimp.audiences.map((audience) => audience.id),
              )}
              id="MailPermission_Mailchimp_Audience"
            >
              {integration.mailchimp.audiences.map((audience, index) => (
                <option {...form.option(index)} key={index}>
                  {audience.name}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages
              attribute={t('mailchimp_audience')}
              errors={errors.integration_config?.mailchimp?.audience_id}
            />
          </UI.InputGroup>
        )}

        {integration?.active_campaign && (
          <UI.InputGroup sc={{
            valid: !errors.integration_config?.active_campaign?.list_id,
            touched: form.touched.integration_config?.active_campaign?.list_id,
          }}
          >
            <UI.InputLabel htmlFor="MailPermission_ActiveCampaign_List">
              {t('active_campaign_list')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.Select
              {...form.select(
                'integration_config.active_campaign.list_id',
                integration.active_campaign.lists.map((list) => list.id),
              )}
              id="MailPermission_ActiveCampaign_List"
            >
              {integration.active_campaign.lists.map((list, index) => (
                <option {...form.option(index)} key={index}>
                  {list.name}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages
              attribute={t('active_campaign_list')}
              errors={errors.integration_config?.active_campaign?.list_id}
            />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_mail_permission')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditMailPermissionForm;
